// CalcVisual.jsx — ELITE VERSION

import { motion } from "framer-motion";
import {
  MapPin,
  CheckCircle,
  BarChart3,
  ArrowUpRight,
} from "lucide-react";

/* ---------------- IMAGES ---------------- */


const HERO_STATUS =
  "https://res.cloudinary.com/dge0knh7q/image/upload/q_auto/f_auto/v1776660975/hero_vr8tlh.svg";

const HERO_AIRBNB =
  "https://res.cloudinary.com/dge0knh7q/image/upload/q_auto/f_auto/v1776660973/airbnb_wvewf3.svg";

const HERO_REPORT =
  "https://res.cloudinary.com/dge0knh7q/image/upload/q_auto/f_auto/v1776660973/revenue_v512zg.svg";

/* ---------------- META ---------------- */

const META = {
  hero_1: {
    eyebrow: "AI Revenue Engine",
    title: "Start analysing your property",
    desc: "Pricing • Demand • Performance",
  },
  hero_2: {
    eyebrow: "Listing analysis",
    title: "Analyse your Airbnb listing",
    desc: "Extracting pricing signals",
  },
  map: {
    eyebrow: "Location intelligence",
    title: "Pinpoint your property",
    desc: "Map-driven positioning",
  },
  "analytics-live": {
    eyebrow: "Market intelligence",
    title: "Analysing real-time demand",
    desc: "Live pricing & occupancy",
  },
  report: {
    eyebrow: "Owner report",
    title: "Your report is ready",
    desc: "Insights & optimisation",
  },
};

/* ---------------- MAIN ---------------- */

export default function CalcVisual({ mode = "hero_1", data = {} }) {
  const meta = META[mode];

  return (
    <div className="absolute inset-0 overflow-hidden">

      <motion.div
        key={mode}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0"
      >
        {mode === "map" && <MapScene data={data} meta={meta} />}
        {mode === "analytics-live" && <Analytics meta={meta} />}
        {(mode === "hero_1" || mode === "hero_2" || mode === "report") && (
          <ImageScene
            img={
              mode === "hero_1"
                ? HERO_STATUS
                : mode === "hero_2"
                ? HERO_AIRBNB
                : HERO_REPORT
            }
            meta={meta}
          />
        )}
      </motion.div>

    </div>
  );
}

/* ================= CTA ================= */

function CTA({ children }) {
  return (
    <button className="
      rounded-pill px-3 py-1.5 text-[11px] font-semibold
      border border-accent-gold/30 bg-accent-gold/10 text-accent-gold
      transition-all duration-200
      hover:bg-accent-gold/20 active:scale-[0.98]
    ">
      {children}
    </button>
  );
}

/* ================= IMAGE ================= */

function ImageScene({ img, meta }) {
  return (
    <div className="absolute inset-0">

      <img src={img} className="w-full h-full object-cover" />

      {/* CLEAN OVERLAY */}
      <div className="absolute inset-0 bg-black/50" />

      {/* TOP */}
      <div className="absolute top-4 left-4">
        <div className="
          flex items-center gap-2
          rounded-pill px-3 py-1
          bg-black/40 backdrop-blur-sm
          text-white text-[11px]
        ">
          <span className="w-2 h-2 rounded-full bg-accent-gold" />
          {meta.eyebrow}
        </div>
      </div>

      {/* BOTTOM */}
      <div className="absolute bottom-0 w-full p-3 sm:p-4">

        <div className="
          bg-black/40 backdrop-blur-sm
          border border-white/10
          rounded-2xl p-3 sm:p-4
          transition-all duration-300
        ">

          <div className="flex justify-between items-start gap-3">
            <div>
              <h3 className="text-white font-semibold text-sm sm:text-base">
                {meta.title}
              </h3>
              <p className="text-white/70 text-xs mt-1">
                {meta.desc}
              </p>
            </div>

            <ArrowUpRight size={14} className="text-accent-gold" />
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Stat label="Revenue" value="↑" />
            <Stat label="Demand" value="Live" />
            <Stat label="Focus" value="Gold" />
          </div>

        </div>

      </div>

    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl py-2">
      <div className="text-white text-[11px] font-semibold">{value}</div>
      <div className="text-white/60 text-[10px]">{label}</div>
    </div>
  );
}

/* ================= MAP ================= */

function MapScene({ data, meta }) {
  const lat = data?.lat;
  const lng = data?.lng;
  const delta = 0.04;

  const src =
    lat && lng
      ? `https://www.openstreetmap.org/export/embed.html?bbox=${
          lng - delta
        }%2C${lat - delta}%2C${lng + delta}%2C${lat + delta}&marker=${lat}%2C${lng}`
      : null;

  return (
    <div className="absolute inset-0">

      {src ? (
        <iframe
          src={src}
          className="absolute inset-0 w-full h-full border-0"
        />
      ) : (
        <div className="absolute inset-0 bg-brand-secondary" />
      )}

      <div className="absolute inset-0 bg-black/20" />

      {/* TOP */}
      <div className="absolute top-4 left-4 right-4">
        <div className="
          flex justify-between items-center
          bg-black/40 backdrop-blur-sm
          border border-white/10
          rounded-xl px-3 py-2 text-white text-xs
        ">
          <div className="flex items-center gap-2 truncate">
            <MapPin size={14} className="text-accent-gold" />
            {data?.location || "Select location"}
          </div>

          {lat && (
            <span className="text-accent-gold flex items-center gap-1">
              <CheckCircle size={12} /> Active
            </span>
          )}
        </div>
      </div>

      {/* BOTTOM */}
      <div className="absolute bottom-0 w-full p-3">
        <div className="
          bg-black/40 backdrop-blur-sm
          border border-white/10
          rounded-xl p-3 text-white
        ">
          <p className="text-[11px] text-white/60">{meta.eyebrow}</p>
          <p className="font-semibold">{meta.title}</p>

          <div className="mt-2">
            <CTA>Refine</CTA>
          </div>
        </div>
      </div>

    </div>
  );
}

/* ================= ANALYTICS ================= */

function Analytics({ meta }) {
  return (
    <div className="
      absolute inset-0
      bg-brand-secondary
      p-4 flex flex-col justify-between
    ">

      {/* HEADER */}
      <div className="flex justify-between text-white text-xs">
        <span className="flex items-center gap-2">
          <BarChart3 size={14} className="text-accent-gold" />
          {meta.eyebrow}
        </span>

        <span className="text-accent-gold flex items-center gap-1">
          <span className="w-2 h-2 bg-accent-gold rounded-full animate-pulse" />
          Updating
        </span>
      </div>

      {/* TITLE */}
      <div>
        <h3 className="text-white text-lg font-bold">{meta.title}</h3>
        <p className="text-white/70 text-sm">{meta.desc}</p>
      </div>

      {/* METERS */}
      <div className="grid grid-cols-4 gap-2 h-[80px]">
        {[60, 80, 50, 90].map((v, i) => (
          <div key={i} className="flex flex-col items-center justify-end">
            <div
              className="w-full bg-accent-gold/80 rounded"
              style={{ height: `${v}%` }}
            />
            <span className="text-[10px] text-white/60">
              {["Occ","Rev","ADR","Imp"][i]}
            </span>
          </div>
        ))}
      </div>

      {/* FOOT */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <Stat label="Demand" value="Live" />
        <Stat label="Pricing" value="Adaptive" />
        <Stat label="Report" value="Ready" />
      </div>

    </div>
  );
}